import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Play, Pause, ChevronDown, ChevronUp, Clock, ExternalLink, Mic, Volume2, Download, Copy, Check } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../services/AuthContext';
import { sessionService } from '../services/sessionService';
import { useAudio } from '../contexts/AudioContext';
import { useAudioUI } from '../contexts/AudioUIContext';
import { useWidgetExpansion } from '../hooks/useWidgetExpansion';
import { useTranscriptCache } from '../hooks/useTranscriptCache';
import { useShareLink } from '../contexts/ShareLinkContext';

// Helper function to safely decode URI components
const safeDecodeURIComponent = (str: string): string => {
  try {
    return decodeURIComponent(str);
  } catch (e) {
    console.error('[AudioClipCard] Failed to decode URI:', str, e);
    return str; // Return original string if decoding fails
  }
};

interface AudioClipData {
  sessionId: string;
  startTime: string;
  endTime?: string;
  name?: string;
  snippetName: string;
  narrativeContext?: string; // Optional narrative context for the clip
}

interface AudioClipCardProps {
  clipData: AudioClipData;
  onLoadSession: (sessionId: string) => void;
  currentSessionId?: string | null;
  isPlaying?: boolean;
  isCurrentAudio?: boolean;
  onPlay?: (sessionId: string, startTime: number, endTime?: number) => void;
  onStop?: () => void;
  onHoverTimeRange?: (startTime: number, endTime?: number) => void;
  onHoverEnd?: () => void;
}

interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  speaker?: string;
  speakerLabel?: string;
}

const AudioClipCard: React.FC<AudioClipCardProps> = ({
  clipData,
  onLoadSession,
  currentSessionId,
  isPlaying = false,
  isCurrentAudio = false,
  onPlay,
  onStop,
  onHoverTimeRange,
  onHoverEnd
}) => {
  const { isDarkMode } = useDarkMode();
  const { token } = useAuth();
  const { shareToken } = useShareLink();
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string>('');
  const { seekTo, play, pause, playSegment } = useAudio();
  const { playingWidgetId, setPlayingWidgetId: contextSetPlayingWidgetId } = useAudioUI();
  const [isCopied, setIsCopied] = useState(false);
  const [shouldCopyAfterLoad, setShouldCopyAfterLoad] = useState(false);
  const [shouldGenerateTTMLAfterLoad, setShouldGenerateTTMLAfterLoad] = useState(false);
  
  const { sessionId, startTime, endTime, name, snippetName, narrativeContext } = clipData;

  // Convert HH:MM:SS to seconds
  const timeToSeconds = (timeStr: string): number => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  const startSeconds = timeToSeconds(startTime);
  const endSeconds = endTime ? timeToSeconds(endTime) : undefined;
  const duration = endSeconds ? endSeconds - startSeconds : 0;
  const isRange = !!endTime;
  const isCurrentSession = sessionId === (currentSessionId || '');
  
  const widgetId = `widget_${sessionId}_${startSeconds}_${endSeconds || 'single'}`;
  const isThisWidgetPlaying = playingWidgetId === widgetId;
  
  // Use custom hooks for persistent state across re-renders
  const { isExpanded: localIsExpanded, setExpanded: setLocalIsExpanded } = useWidgetExpansion(widgetId);
  const { transcriptSegments, speakerLabels, cacheTranscript, hasCache } = useTranscriptCache(widgetId);

  // Format seconds to HH:MM:SS
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handlePlayClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isCurrentSession) {
      if (isThisWidgetPlaying) {
        // Stop playing
        if (onStop) onStop();
        pause();
      } else {
        // Start playing this widget
        if (onPlay) {
          contextSetPlayingWidgetId(widgetId);
          onPlay(sessionId, startSeconds, endSeconds);
        } else {
          await playSegment(startSeconds, endSeconds);
        }
      }
    } else {
      // For other sessions, use the onLoadSession callback
      onLoadSession(sessionId);
    }
  };

  const loadTranscript = useCallback(async () => {
    // Check if already cached
    if (hasCache() || isLoadingTranscript) {
      return;
    }
    
    setIsLoadingTranscript(true);
    setTranscriptError('');
    
    try {
      const sessionData = await sessionService.getSession(sessionId, token || shareToken || undefined);
      
      if (sessionData.transcriptionData?.results?.utterances) {
        // Filter utterances within our time range
        const filteredUtterances = sessionData.transcriptionData.results.utterances
          .filter((utterance: any) => {
            const uttStart = utterance.start;
            const uttEnd = utterance.end;
            // Include utterances that overlap with our time range
            return uttEnd >= startSeconds && (endSeconds ? uttStart <= endSeconds : uttStart <= startSeconds + 10);
          })
          .map((utterance: any) => {
            const speakerNum = utterance.speaker;
            const speakerLabel = sessionData.speakerLabels?.[speakerNum] || `Speaker ${speakerNum}`;
            return {
              start: utterance.start,
              end: utterance.end,
              text: utterance.transcript,
              speaker: speakerNum !== undefined ? `Speaker ${speakerNum}` : undefined,
              speakerLabel: speakerNum !== undefined ? speakerLabel : undefined
            };
          });
        
        // Cache the transcript data
        cacheTranscript(filteredUtterances, sessionData.speakerLabels || {});
      } else {
        setTranscriptError('No transcript data available');
      }
    } catch (error) {
      console.error('Failed to load transcript:', error);
      setTranscriptError('Failed to load transcript');
    } finally {
      setIsLoadingTranscript(false);
    }
  }, [sessionId, token, startSeconds, endSeconds, isLoadingTranscript, hasCache, cacheTranscript]);

  const handleExpandClick = useCallback(async () => {
    const newExpanded = !localIsExpanded;
    setLocalIsExpanded(newExpanded);
    
    // Load transcript if expanding and not already loaded
    if (newExpanded && transcriptSegments.length === 0 && !isLoadingTranscript) {
      await loadTranscript();
    }
  }, [localIsExpanded, transcriptSegments.length, isLoadingTranscript, loadTranscript, setLocalIsExpanded]);

  const handleLoadSessionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open in new tab for attached sessions
    window.open(`/session/${sessionId}`, '_blank');
  };
  
  // Generate TTML for this clip
  const generateClipTTML = useCallback(async () => {
    // Load transcript if not loaded
    if (transcriptSegments.length === 0) {
      setShouldGenerateTTMLAfterLoad(true);
      if (!isLoadingTranscript) {
        await loadTranscript();
      }
      return;
    }
    const formatTimeForTTML = (seconds: number): string => {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = (seconds % 60).toFixed(3);
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(6, '0')}`;
    };
    
    // XML escape function to handle special characters
    const escapeXml = (text: string): string => {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
    };
    
    const ttmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<tt xml:lang="en" xmlns="http://www.w3.org/ns/ttml" xmlns:ttm="http://www.w3.org/ns/ttml#metadata" xmlns:tts="http://www.w3.org/ns/ttml#styling" xmlns:ttp="http://www.w3.org/ns/ttml#parameter" xmlns:ittp="http://www.w3.org/ns/ttml/profile/imsc1#parameter" xmlns:itts="http://www.w3.org/ns/ttml/profile/imsc1#styling" ttp:profile="http://www.w3.org/ns/ttml/profile/imsc1/text" ttp:frameRate="24" ttp:frameRateMultiplier="1000 1001" ttp:dropMode="nonDrop" ttp:timeBase="media">
  <head>
    <styling>
      <style xml:id="clip_style" tts:color="#ffffff" tts:fontSize="100%" tts:fontFamily="proportionalSansSerif" tts:fontWeight="bold" tts:textAlign="center"/>
      <style xml:id="transcript_style" tts:color="#ffffff" tts:fontSize="90%" tts:fontFamily="proportionalSansSerif" tts:textAlign="left"/>
      <style xml:id="description_style" tts:color="#ffff00" tts:fontSize="85%" tts:fontFamily="proportionalSansSerif" tts:textAlign="center"/>
    </styling>
    <layout>
      <region xml:id="r_clip" tts:origin="10% 10%" tts:extent="80% 20%" tts:displayAlign="before"/>
      <region xml:id="r_transcript" tts:origin="10% 75%" tts:extent="80% 20%" tts:displayAlign="after"/>
      <region xml:id="r_description" tts:origin="10% 45%" tts:extent="80% 15%" tts:displayAlign="center"/>
    </layout>
    <metadata>
      <ttm:title>${escapeXml(snippetName)}</ttm:title>
      <ttm:agent xml:id="ai">AI Assistant</ttm:agent>
    </metadata>
  </head>
  <body>
    <div xml:id="d_clip" region="r_clip" style="clip_style">
      <p begin="${formatTimeForTTML(startSeconds)}" end="${formatTimeForTTML(endSeconds || startSeconds + 10)}">${escapeXml(snippetName)}</p>
    </div>
    <div xml:id="d_transcript" region="r_transcript" style="transcript_style">
      ${transcriptSegments.map((seg, idx) => 
        `<p begin="${formatTimeForTTML(seg.start)}" end="${formatTimeForTTML(seg.end)}">${escapeXml(seg.speakerLabel || seg.speaker || 'Unknown')}: ${escapeXml(seg.text)}</p>`
      ).join('\n      ')}
    </div>
    ${name ? `<div xml:id="d_description" region="r_description" style="description_style">
      <p begin="${formatTimeForTTML(startSeconds)}" end="${formatTimeForTTML(endSeconds || startSeconds + 10)}">${escapeXml(safeDecodeURIComponent(name))}</p>
    </div>` : ''}
  </body>
</tt>`;
    
    const blob = new Blob([ttmlContent], { type: 'application/ttml+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${snippetName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ttml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [transcriptSegments, snippetName, startSeconds, endSeconds, name, isLoadingTranscript, loadTranscript]);

  const getSessionDisplayId = () => {
    return sessionId.length > 8 ? `${sessionId.substring(0, 8)}...` : sessionId;
  };
  
  // Copy transcript to clipboard
  const copyTranscriptToClipboard = useCallback(async () => {
    if (transcriptSegments.length === 0) {
      // Load transcript first if not loaded
      setShouldCopyAfterLoad(true);
      if (!isLoadingTranscript) {
        await loadTranscript();
      }
      return;
    }
    
    const transcriptText = transcriptSegments.map(seg => 
      `[${formatTime(seg.start)} - ${formatTime(seg.end)}] ${seg.speakerLabel || seg.speaker || 'Unknown'}: ${seg.text}`
    ).join('\n');
    
    const fullText = `${snippetName}\n${startTime} → ${endTime || startTime}\n${name ? safeDecodeURIComponent(name) + '\n' : ''}\n${transcriptText}`;
    
    try {
      // Check if clipboard API is available
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(fullText);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = fullText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy transcript:', error);
    }
  }, [transcriptSegments, snippetName, startTime, endTime, name, formatTime, isLoadingTranscript, loadTranscript]);

  // Use effect to load transcript when expanded, but only once
  useEffect(() => {
    if (transcriptSegments.length === 0 && localIsExpanded && !isLoadingTranscript) {
      loadTranscript();
    }
  }, [localIsExpanded]); // Only depend on expansion state, not transcript data
  
  // Effect to copy transcript after it loads
  useEffect(() => {
    if (shouldCopyAfterLoad && transcriptSegments.length > 0 && !isLoadingTranscript) {
      setShouldCopyAfterLoad(false);
      copyTranscriptToClipboard();
    }
  }, [shouldCopyAfterLoad, transcriptSegments.length, isLoadingTranscript, copyTranscriptToClipboard]);
  
  // Effect to generate TTML after transcript loads
  useEffect(() => {
    if (shouldGenerateTTMLAfterLoad && transcriptSegments.length > 0 && !isLoadingTranscript) {
      setShouldGenerateTTMLAfterLoad(false);
      generateClipTTML();
    }
  }, [shouldGenerateTTMLAfterLoad, transcriptSegments.length, isLoadingTranscript, generateClipTTML]);
  
  return (
    <div className={`
      w-full my-4 rounded-xl overflow-hidden transition-all duration-300
      ${isDarkMode 
        ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 shadow-xl' 
        : 'bg-gradient-to-br from-[#f6f1e5] to-[#eef2f6] border border-[#d8c9a3] shadow-lg'
      }
      ${isThisWidgetPlaying ? 'ring-2 ring-brand-yellow ring-offset-2' : ''}
    `}>
      {/* Header Section */}
      <div className={`
        p-4 cursor-pointer transition-colors duration-200
        ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-white/50'}
      `}
        onClick={handleExpandClick}
        onMouseEnter={() => {
          if (isCurrentSession && onHoverTimeRange) {
            onHoverTimeRange(startSeconds, endSeconds);
          }
        }}
        onMouseLeave={() => {
          if (isCurrentSession && onHoverEnd) {
            onHoverEnd();
          }
        }}
      >
        <div className="flex items-start gap-4">
          {/* Play Button */}
          <button
            onClick={handlePlayClick}
            className={`
              flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200
              ${isThisWidgetPlaying
                ? 'bg-gradient-to-br from-brand-yellow to-[#d9980e] hover:from-[#ffc54d] hover:to-brand-yellow shadow-lg text-brand-navy' 
                : 'bg-gradient-to-br from-brand-navy to-[#41526a] hover:from-[#1a2230] hover:to-brand-navy shadow-lg'
              }
              ${isThisWidgetPlaying ? '' : 'text-white'} transform hover:scale-105
            `}
            title={isThisWidgetPlaying ? 'Pause' : 'Play'}
          >
            {isThisWidgetPlaying ? (
              <Pause size={20} />
            ) : (
              <Play size={20} className="ml-1" />
            )}
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <h3 className={`
              text-lg font-semibold mb-1
              ${isDarkMode ? 'text-white' : 'text-gray-900'}
            `}>
              {snippetName}
            </h3>

            {/* Time Info */}
            <div className="flex items-center gap-4 mb-2">
              <div className={`
                flex items-center gap-1.5 text-sm
                ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}
              `}>
                <Clock size={14} />
                {isRange ? (
                  <span>{startTime} → {endTime} <span className="opacity-75">({Math.floor(duration)}s)</span></span>
                ) : (
                  <span>{startTime}</span>
                )}
              </div>

              {!isCurrentSession && (
                <div className="flex items-center gap-1.5 text-sm text-brand-yellow">
                  <ExternalLink size={14} />
                  <span>Session {getSessionDisplayId()}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {name && (
              <p className={`
                text-sm mb-2
                ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
              `}>
                {safeDecodeURIComponent(name)}
              </p>
            )}

            {/* Narrative Context */}
            {narrativeContext && (
              <div className={`
                text-sm italic p-3 rounded-lg mt-2
                ${isDarkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-white/50 text-gray-700'}
              `}>
                <Mic size={14} className="inline mr-2 opacity-70" />
                {narrativeContext}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-start gap-2">
            {/* Expand/Collapse Button */}
            <button
              className={`
                p-2 rounded-lg transition-colors duration-200
                ${isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                  : 'hover:bg-white/70 text-gray-600 hover:text-gray-800'
                }
              `}
              onClick={(e) => {
                e.stopPropagation();
                handleExpandClick();
              }}
              title={localIsExpanded ? "Collapse transcript" : "Expand transcript"}
            >
              {localIsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {/* Copy Transcript Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyTranscriptToClipboard();
              }}
              className={`
                p-2 rounded-lg transition-all duration-200 relative
                ${isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                  : 'hover:bg-white/70 text-gray-600 hover:text-gray-800'
                }
                ${isCopied ? 'text-brand-yellow' : ''}
              `}
              title={isCopied ? "Copied!" : "Copy transcript with timestamps"}
            >
              {isCopied ? (
                <Check size={18} className="animate-bounce" />
              ) : (
                <Copy size={18} />
              )}
            </button>

            {/* Download TTML Button */}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                await generateClipTTML();
              }}
              className={`
                p-2 rounded-lg transition-colors duration-200
                ${isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                  : 'hover:bg-white/70 text-gray-600 hover:text-gray-800'
                }
              `}
              title="Download TTML"
              disabled={transcriptSegments.length === 0 && !localIsExpanded}
            >
              <Download size={18} />
            </button>
            
            {/* Load Session Button (only for different sessions) */}
            {!isCurrentSession && (
              <button
                onClick={handleLoadSessionClick}
                className={`
                  p-2 rounded-lg transition-colors duration-200
                  ${isDarkMode 
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                    : 'hover:bg-white/70 text-gray-600 hover:text-gray-800'
                  }
                `}
                title="Open Session in New Tab"
              >
                <ExternalLink size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Transcript Section */}
      {localIsExpanded && (
        <div className={`
          border-t transition-all duration-300
          ${isDarkMode ? 'border-gray-700' : 'border-[#d8c9a3]'}
        `}>
          <div className={`
            p-4
            ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/30'}
          `}>
            {isLoadingTranscript ? (
              <div className="text-center py-8">
                <div className="animate-pulse text-sm opacity-70">Loading transcript...</div>
              </div>
            ) : transcriptError ? (
              <div className="text-center py-8 text-red-500 text-sm">{transcriptError}</div>
            ) : transcriptSegments.length === 0 ? (
              <div className="text-center py-8 opacity-70 text-sm">No transcript available for this segment</div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {transcriptSegments.map((segment, index) => {
                  const speakerNumber = segment.speaker ? parseInt(segment.speaker.replace('Speaker ', '')) : -1;
                  const avatarColors = [
                    'from-brand-navy to-[#41526a]',
                    'from-[#5b6778] to-[#2f394a]',
                    'from-brand-yellow to-[#d9980e]',
                    'from-[#c7b07a] to-[#8c6b2f]',
                    'from-[#d8d1c2] to-[#a2acb8]',
                    'from-[#41526a] to-[#232E3F]'
                  ];
                  const avatarColor = avatarColors[speakerNumber % avatarColors.length];
                  
                  return (
                    <div key={index} className="flex gap-3">
                      {/* Speaker Avatar */}
                      <div className="flex-shrink-0">
                        <div className={`
                          w-10 h-10 rounded-full bg-gradient-to-br ${segment.speaker ? avatarColor : 'from-gray-400 to-gray-600'} 
                          flex items-center justify-center text-white text-sm font-bold shadow-md
                        `}>
                          {segment.speakerLabel ? segment.speakerLabel.charAt(0).toUpperCase() : '?'}
                        </div>
                      </div>
                      
                      {/* Message Content */}
                      <div className="flex-1">
                        {/* Speaker Name & Time */}
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className={`
                            text-sm font-medium
                            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                          `}>
                            {segment.speakerLabel || segment.speaker || 'Unknown Speaker'}
                          </span>
                          <span className={`
                            text-xs opacity-60
                            ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                          `}>
                            {formatTime(segment.start)}
                          </span>
                        </div>
                        
                        {/* Transcript Text */}
                        <div className={`
                          text-sm leading-relaxed
                          ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}
                        `}>
                          {segment.text}
                        </div>
                      </div>

                      {/* Play Segment Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCurrentSession) {
                            if (onPlay) {
                              const segmentWidgetId = `widget_${sessionId}_${segment.start}_${segment.end}`;
                            contextSetPlayingWidgetId(segmentWidgetId);
                            onPlay(sessionId, segment.start, segment.end);
                            } else {
                              playSegment(segment.start, segment.end);
                            }
                          } else {
                            // Open in new tab with time parameter
                            const timeStr = `${Math.floor(segment.start / 60)}m${Math.floor(segment.start % 60)}s`;
                            window.open(`/session/${sessionId}?t=${timeStr}`, '_blank');
                          }
                        }}
                        className={`
                          flex-shrink-0 p-1.5 rounded-lg opacity-0 hover:opacity-100 transition-opacity duration-200
                          ${isDarkMode
                            ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                            : 'hover:bg-gray-200 text-gray-600 hover:text-gray-800'
                          }
                        `}
                        title="Play this segment"
                      >
                        <Volume2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Export the non-memoized component for better control
export { AudioClipCard };

const MemoizedAudioClipCard = memo(AudioClipCard, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  // Only compare the essential data props
  const prevData = {
    sessionId: prevProps.clipData.sessionId,
    startTime: prevProps.clipData.startTime,
    endTime: prevProps.clipData.endTime,
    name: prevProps.clipData.name,
    snippetName: prevProps.clipData.snippetName,
    currentSessionId: prevProps.currentSessionId,
    isPlaying: prevProps.isPlaying,
    isCurrentAudio: prevProps.isCurrentAudio
  };
  
  const nextData = {
    sessionId: nextProps.clipData.sessionId,
    startTime: nextProps.clipData.startTime,
    endTime: nextProps.clipData.endTime,
    name: nextProps.clipData.name,
    snippetName: nextProps.clipData.snippetName,
    currentSessionId: nextProps.currentSessionId,
    isPlaying: nextProps.isPlaying,
    isCurrentAudio: nextProps.isCurrentAudio
  };
  
  // Don't re-render if only callbacks changed
  return JSON.stringify(prevData) === JSON.stringify(nextData);
});

export default MemoizedAudioClipCard;

// Helper function for memo comparison
function timeToSeconds(timeStr: string): number {
  const [hours, minutes, seconds] = timeStr.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}
